// utils/shippingSubsidy.ts — Subsidi/gratis-ongkir-maks (1 sumber kebenaran, frontend)
// Kebijakan ongkir form ada 3: 'bayar' | 'gratis' | 'subsidi'.
// - bayar   = shippingSettings.regular.visible
// - gratis  = free.visible && !regular.visible && !maxSubsidy
// - subsidi = free.visible && !regular.visible && maxSubsidy > 0
// Rumus: bayar = max(0, tarifEverpro - maxSubsidy). Contoh: tarif 10rb & maks 15rb → 0;
// tarif 20rb & maks 15rb → bayar 5rb. Backend (wa-cuanmax/utils/formShippingHelpers.js) mirror rumus ini.
import type { Form } from '../types';

export type ShippingPolicy = 'bayar' | 'gratis' | 'subsidi';

export function getMaxSubsidy(form: Form | null | undefined): number {
    const raw = (form?.shippingSettings?.free as { maxSubsidy?: number | null } | undefined)?.maxSubsidy;
    const n = Number(raw) || 0;
    return n > 0 ? Math.round(n) : 0;
}

export function getShippingPolicy(form: Form | null | undefined): ShippingPolicy {
    const isGratisLike = !!(form?.shippingSettings?.free?.visible) && !form?.shippingSettings?.regular?.visible;
    if (!isGratisLike) return 'bayar';
    if (getSubsidyScope(form) === 'varian') {
        return getLargestSubsidy(form) > 0 ? 'subsidi' : 'gratis';
    }
    return getMaxSubsidy(form) > 0 ? 'subsidi' : 'gratis';
}

export type SubsidyScope = 'produk' | 'varian';

export type SubsidyMultiply = 'once' | 'perQty';

// Cakupan kali qty: 'once' (default) = maks berlaku 1x per pesanan;
// 'perQty' = maks dikali jumlah beli. Disimpan sebagai boolean
// shippingSettings.free.subsidyPerQty (true = perQty).
export function getSubsidyPerQty(form: Form | null | undefined): boolean {
    return (form?.shippingSettings?.free as { subsidyPerQty?: unknown } | undefined)?.subsidyPerQty === true;
}

export function getSubsidyMultiply(form: Form | null | undefined): SubsidyMultiply {
    return getSubsidyPerQty(form) ? 'perQty' : 'once';
}

// Level subsidi form (default 'produk' = perilaku lama: satu angka semua varian).
export function getSubsidyScope(form: Form | null | undefined): SubsidyScope {
    const raw = (form?.shippingSettings?.free as { subsidyScope?: unknown } | undefined)?.subsidyScope;
    return raw === 'varian' ? 'varian' : 'produk';
}

// Samakan label varian seperti backend findVariantWeight: "Pilih Warna: Hijau"
// atau cukup "Hijau" tetap ketemu.
function comboMatchesLabel(combo: { attributes?: Record<string, string> }, label: string): boolean {
    const v = String(label || '').trim().toLowerCase();
    if (!v) return false;
    const attrs = combo?.attributes || {};
    const entries = Object.entries(attrs);
    const full = entries.map(([k, val]) => `${k}: ${val}`).join(', ').toLowerCase();
    if (full === v) return true;
    if (Object.values(attrs).some(val => String(val).toLowerCase() === v)) return true;
    return full.includes(v);
}

// Cari kombinasi varian dari label order ("Hijau" / "Pilih Warna: Hijau")
// atau dari object attributes. Return null bila tidak ketemu.
export function findVariantCombination(
    form: Form | null | undefined,
    variant?: string | Record<string, string> | { attributes?: Record<string, string> } | null,
): { maxSubsidy?: number | null } | null {
    const combos = (form as { variantCombinations?: Array<{ attributes?: Record<string, string>; maxSubsidy?: number | null }> } | null | undefined)?.variantCombinations;
    if (!combos?.length || variant == null) return null;
    if (typeof variant === 'object') {
        const attrs = (variant as { attributes?: Record<string, string> }).attributes ?? (variant as Record<string, string>);
        const keys = Object.keys(attrs || {});
        if (!keys.length) return null;
        const hit = combos.find(c => keys.every(k => (c.attributes || {})[k] === (attrs as Record<string, string>)[k]));
        if (hit) return hit;
        // fallback: cocokkan by values saja
        const vals = Object.values(attrs as Record<string, string>).map(s => String(s).toLowerCase());
        return combos.find(c => Object.values(c.attributes || {}).some(val => vals.includes(String(val).toLowerCase()))) ?? null;
    }
    const label = String(variant);
    return combos.find(c => comboMatchesLabel(c, label)) ?? null;
}

// Subsidi efektif untuk satu varian: scope 'varian' + combo.maxSubsidy != null
// → pakai angka varian (0 eksplisit = tanpa subsidi); selain itu fallback induk.
// `combination` boleh object kombinasi, label string, atau null (tanpa varian).
export function getEffectiveMaxSubsidy(
    form: Form | null | undefined,
    combination?: { maxSubsidy?: number | null; attributes?: Record<string, string> } | string | Record<string, string> | null,
    qty?: number | null,
): number {
    const base = getBaseMaxSubsidy(form, combination);
    const q = Math.max(1, Math.floor(Number(qty) || 1));
    return getSubsidyPerQty(form) ? base * q : base;
}

// Angka dasar sebelum dikali qty (dipakai display per-pcs + policy check).
export function getBaseMaxSubsidy(
    form: Form | null | undefined,
    combination?: { maxSubsidy?: number | null; attributes?: Record<string, string> } | string | Record<string, string> | null,
): number {
    const induk = getMaxSubsidy(form);
    if (getSubsidyScope(form) !== 'varian') return induk;
    if (combination == null) return induk;
    const combo = (typeof combination === 'object' && 'sellingPrice' in (combination as object))
        ? (combination as { maxSubsidy?: number | null })
        : findVariantCombination(form, combination as string | Record<string, string>);
    if (!combo || combo.maxSubsidy == null) return induk;
    const n = Number(combo.maxSubsidy) || 0;
    return n > 0 ? Math.round(n) : 0;
}

// Nilai subsidi terbesar di form (untuk copy konsultasi/herbal saat varian belum dipilih).
export function getLargestSubsidy(form: Form | null | undefined, qty?: number | null): number {
    const induk = getMaxSubsidy(form);
    let base = induk;
    if (getSubsidyScope(form) === 'varian') {
        const combos = (form as { variantCombinations?: Array<{ maxSubsidy?: number | null }> } | null | undefined)?.variantCombinations || [];
        for (const c of combos) {
            const n = Number(c?.maxSubsidy) || 0;
            if (n > base) base = Math.round(n);
        }
    }
    const q = Math.max(1, Math.floor(Number(qty) || 1));
    return getSubsidyPerQty(form) ? base * q : base;
}

export function applyShippingSubsidy(ratePrice: number, maxSubsidy: number): { pay: number; subsidy: number } {
    const price = Number(ratePrice) || 0;
    const maks = Number(maxSubsidy) || 0;
    if (maks <= 0) return { pay: price, subsidy: 0 };
    const subsidy = Math.min(price, Math.round(maks));
    return { pay: Math.max(0, price - subsidy), subsidy };
}
